import { useEffect, useState } from "react";
import { ChevronUp, ChevronDown, Download } from "lucide-react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { toast } from "react-toastify";
import { API_ROUTES } from "config/api-config";
import { MapPin } from "lucide-react";
import Select from "react-select";

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { Users, UserMinus } from "lucide-react";
import "./persession.css";
import PresessionSelect from "./PresessionSelect";

// ✅ FIX: Import the named functions instead of default axios
import {
  get_session_attendance_status,
  get_locations_by_date,
  download_attendance_pdf,
} from "services/api-service";

// Dummy data
const dummyData = {
  "Main Campus": {
    totalStudents: 950,
    sessions: [
      {
        id: 101,
        date: "2025-08-26",
        name: "Session 1",
        present: 860,
        absent: 90,
        students: [
          { id: "STU001", name: "Amit Kumar", status: "Present" },
          { id: "STU002", name: "Riya Sharma", status: "Absent" },
        ],
      },
      {
        id: 102,
        date: "2025-08-27",
        name: "Session 2",
        present: 872,
        absent: 78,
        students: [
          { id: "STU003", name: "Rahul Verma", status: "Present" },
          { id: "STU004", name: "Sneha Patel", status: "Present" },
        ],
      },
    ],
  },
  "Annex Campus": {
    totalStudents: 480,
    sessions: [
      {
        id: 201,
        date: "2025-08-26",
        name: "Session 1",
        present: 420,
        absent: 60,
        students: [
          { id: "STU005", name: "Kunal Mehra", status: "Present" },
          { id: "STU006", name: "Nisha Yadav", status: "Absent" },
        ],
      },
    ],
  },
};

export default function Persession() {
  const history = useHistory();
  const [campus, setCampus] = useState("");
  const user = localStorage.getItem("selectData"); // ✅ Correct key
  const [selectedLocation, setSelectedLocation] = useState("Main Campus");
  const [expandedSession, setExpandedSession] = useState(null);
  const [showSessionReport, setShowSessionReport] = useState(true);
  const [selectLocation, setSelectLocation] = useState([]);
  const [datas, setDatas] = useState(null);
  const [sessionsS, setSessions] = useState([]);

  const today = new Date();
  const formattedToday = today.toISOString().split("T")[0];
  const [selectedDate, setSelectedDate] = useState(
    localStorage.getItem("selectData") || formattedToday
  );

  // Reset when user changes
  useEffect(() => {
    setCampus("");
    setSelectLocation([]);
  }, [user]);

  // ✅ Fetch available locations using named function
  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        console.log("📍 Fetching locations for date:", user);
        const res = await get_locations_by_date({ date: user });

        const locations = res.data.locations || [];
        console.log("✅ Locations fetched:", locations);
        setSelectLocation(locations);

        if (locations.length > 0) {
          toast.info(`Found ${locations.length} locations`);
        } else {
          toast.warn("No locations found for this date.");
        }
      } catch (err) {
        console.error(
          "❌ Failed to fetch locations:",
          err.response?.data || err.message
        );
        setSelectLocation([]);
        toast.error("Failed to fetch locations.");
      }
    };

    fetchData();
  }, [user]);

  // Auto-select first location
  useEffect(() => {
    if (Array.isArray(selectLocation) && selectLocation.length > 0) {
      setCampus(selectLocation[0]); // First location string
      console.log("📍 Auto-selected location:", selectLocation[0]);
    }
  }, [selectLocation]);

  // ✅ Fetch session attendance using named function
  useEffect(() => {
    if (!campus || !selectedDate) {
      console.log("⏳ Waiting for campus and date...", { campus, selectedDate });
      return;
    }

    console.log("📤 Fetching sessions for:", { campus, selectedDate });

    get_session_attendance_status({
      location: campus,
      session_id: 0, // Number, not string
      date: selectedDate,
    })
      .then((res) => {
        console.log("✅ Sessions fetched:", res.data);

        if (
          !res.data ||
          (res.data.sessions && res.data.sessions.length === 0)
        ) {
          setDatas(null);
          setSessions([]);
          toast.warn("No sessions found. Please select a new date.");
        } else {
          setDatas(res.data);
          setSessions(res.data.sessions);
          toast.success(`Found ${res.data.sessions.length} sessions`);
        }
      })
      .catch((err) => {
        console.error("❌ API Error:", {
          status: err.response?.status,
          detail: err.response?.data?.detail,
          message: err.message,
        });

        setDatas(null);
        setSessions([]);

        // Better error messages
        if (err.response?.status === 401) {
          toast.error("❌ Session expired. Please login again.");
          localStorage.clear();
        } else if (err.response?.status === 403) {
          toast.error("❌ You don't have permission to view this data.");
        } else if (err.response?.status === 400) {
          toast.error(`❌ Invalid request: ${err.response.data?.detail}`);
        } else {
          toast.error("❌ Failed to fetch sessions.");
        }
      });
  }, [campus, selectedDate]);

  // Download as CSV
  const downloadAsCSV = (session) => {
    const presentStudents = session.present?.data || [];
    const absentStudents = session.absent?.data || [];

    let csvContent = "Session Attendance Report\n";
    csvContent += `Session ID,${session.session_id}\n`;
    csvContent += `Date,${datas.date}\n`;
    csvContent += `Start Time,${session.start_time}\n`;
    csvContent += `End Time,${session.end_time}\n`;
    csvContent += `Total Present,${session.present.total || 0}\n`;
    csvContent += `Total Absent,${session.absent.total || 0}\n\n`;

    csvContent += "PRESENT STUDENTS\n";
    csvContent += "Student ID,Student Name\n";
    presentStudents.forEach((student) => {
      csvContent += `${student.student_id},${student.student_name}\n`;
    });

    csvContent += "\nABSENT STUDENTS\n";
    csvContent += "Student ID,Student Name\n";
    absentStudents.forEach((student) => {
      csvContent += `${student.student_id},${student.student_name}\n`;
    });

    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent)
    );
    element.setAttribute(
      "download",
      `Session_${session.session_id}_${datas.date}.csv`
    );
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    toast.success("Session data downloaded as CSV!");
  };

  // Download as PDF
  const downloadAsPDF = (session) => {
    const presentStudents = session.present?.data || [];
    const absentStudents = session.absent?.data || [];

    let htmlContent = `
      <div style="font-family: Arial, sans-serif; padding: 20px;">
        <h2>Session Attendance Report</h2>
        <p><strong>Session ID:</strong> ${session.session_id}</p>
        <p><strong>Date:</strong> ${datas.date}</p>
        <p><strong>Start Time:</strong> ${session.start_time}</p>
        <p><strong>End Time:</strong> ${session.end_time}</p>
        <hr/>
        <p><strong>Total Present:</strong> ${session.present.total || 0}</p>
        <p><strong>Total Absent:</strong> ${session.absent.total || 0}</p>
        
        <h3>Present Students</h3>
        <table border="1" style="width: 100%; border-collapse: collapse;">
          <tr style="background-color: #f2f2f2;">
            <th style="padding: 8px;">Student ID</th>
            <th style="padding: 8px;">Student Name</th>
          </tr>
          ${presentStudents
            .map(
              (student) =>
                `<tr><td style="padding: 8px;">${student.student_id}</td><td style="padding: 8px;">${student.student_name}</td></tr>`
            )
            .join("")}
        </table>
        
        <h3 style="margin-top: 20px;">Absent Students</h3>
        <table border="1" style="width: 100%; border-collapse: collapse;">
          <tr style="background-color: #f2f2f2;">
            <th style="padding: 8px;">Student ID</th>
            <th style="padding: 8px;">Student Name</th>
          </tr>
          ${absentStudents
            .map(
              (student) =>
                `<tr><td style="padding: 8px;">${student.student_id}</td><td style="padding: 8px;">${student.student_name}</td></tr>`
            )
            .join("")}
        </table>
      </div>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();

    toast.success("Session data ready for PDF export!");
  };

  // Location dropdown options
  let options = [];

  if (Array.isArray(selectLocation) && selectLocation.length === 0) {
    options = [
      {
        value: "no-data",
        label: <div style={{ color: "red", fontWeight: "bold" }}>No Data</div>,
        isDisabled: true,
      },
    ];
  } else if (Array.isArray(selectLocation)) {
    options = selectLocation.map((loc) => ({
      value: loc,
      label: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "5px",
          }}
        >
          <MapPin size={14} color="red" />
          <span>{loc}</span>
        </div>
      ),
    }));
  }

  const customStyles = {
    placeholder: (provided) => ({
      ...provided,
      color: "black",
      fontWeight: "500",
      marginBottom: "5px",
    }),
    control: (provided) => ({
      ...provided,
      width: 200,
      height: 40,
      minHeight: 40,
      margin: "0px",
      paddingTop: "0px",
      paddingBottom: "0px",
      boxSizing: "border-box",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "hsl(0, 0%, 20%)",
      margin: 0,
      padding: 0,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: 0,
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: 0,
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: 0,
    }),
    option: (provided) => ({
      ...provided,
      paddingTop: "0px",
      paddingBottom: "0px",
    }),
  };

  const handleClickTR = () => {
    history.push("dashboard");
  };

  const handleStartDateChange = (e) => {
    setSelectedDate(e.target.value);
    localStorage.setItem("selectData", e.target.value); // Correct key
    console.log("📅 Date changed:", e.target.value);
  };

  return (
    <div>
      {/* Header */}
      <div className="dashboard-headerq">
        <div
          style={{
            paddingLeft: "1%",
            paddingRight: "1%",
            fontSize: "20px",
            fontWeight: "bold",
            borderColor: "black",
          }}
        >
          Attendance Dashboard
        </div>
        <div className="tab-buttonsO">
          <button
            className={`tab-btnO ${!showSessionReport ? "active" : ""}`}
            onClick={() => handleClickTR()}
            style={{ width: "140px", height: "48px" }}
          >
            Overall View
          </button>

          <div className="dashboard-controlss">
            <span style={{ color: "rgba(0, 0, 0, 0.5)", marginLeft: "-3%" }}>
              Date:
            </span>
            <input
              type="date"
              className="date-input"
              value={selectedDate}
              onChange={handleStartDateChange}
              aria-label="Select date"
              style={{ width: "150px", marginLeft: "-3%" }}
            />
          </div>
        </div>
      </div>

      <div style={{ paddingLeft: "1%", marginBottom: "20px", width: "215px" }}>
        <Select
          styles={customStyles}
          options={options}
          placeholder="Select Location"
          value={options.find((o) => o.value === campus) || null}
          onChange={(selected) => {
            setCampus(selected?.value || "");
            console.log("📍 Location selected:", selected?.value);
          }}
          style={{ color: "black" }}
        />
      </div>

      {/* Overall View */}
      {!showSessionReport && (
        <div
          style={{
            paddingLeft: "1%",
            paddingRight: "1%",
            fontSize: "20px",
            fontWeight: "bold",
            borderColor: "black",
            display: "flex",
            justifyContent: "center",
            color: "red",
          }}
        >
          Overall graphs/charts will be displayed here.
        </div>
      )}

      {/* Session Reports */}
      {showSessionReport && (
        <div
          style={{
            paddingLeft: "1%",
            paddingRight: "1%",
            fontSize: "20px",
            fontWeight: "bold",
            borderColor: "black",
          }}
          className="session-grid"
        >
          {options.length > 0 && selectLocation.length > 0 ? (
            <>
              {sessionsS.length > 0 ? (
                sessionsS.map((session) => (
                  <div key={session.session_id} className="session-card">
                    <div className="session-header">
                      <h2 style={{ color: "white" }}>
                        {"Session " + session.session_id}
                      </h2>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          className="download-btn"
                          onClick={() => downloadAsCSV(session)}
                          title="Download as CSV"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                          }}
                        >
                          <Download size={18} color="white" />
                        </button>
                        <button
                          className="toggle-btn"
                          onClick={() =>
                            setExpandedSession(
                              expandedSession === session.session_id
                                ? null
                                : session.session_id
                            )
                          }
                        >
                          {expandedSession === session.session_id ? (
                            <ChevronUp size={18} />
                          ) : (
                            <ChevronDown size={18} />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="session-details">
                      <div className="pills">
                        <span className="pill present">
                          Present: {session.present.total || 0}
                        </span>
                        <span className="pill absent">
                          Absent: {session.absent.total || 0}
                        </span>
                      </div>

                      <div className="session-info">
                        <div>
                          <p>
                            <span>Session ID:</span> {session.session_id}
                          </p>
                          <p>
                            <span>Date:</span> {datas.date}
                          </p>
                          <p style={{ marginTop: "-16px" }}>
                            <span style={{ marginTop: "-16px" }}>
                              Start Time:
                            </span>{" "}
                            {session.start_time}
                          </p>
                          <p style={{ marginTop: "-17px" }}>
                            <span>End Time:</span> {session.end_time}
                          </p>
                        </div>
                        <button
                          className="view-btn"
                          onClick={() => setExpandedSession(session.session_id)}
                        >
                          View Students
                        </button>
                      </div>

                      {expandedSession === session.session_id && (
                        <div className="students-list">
                          {/* Present Students */}
                          <div>
                            <h4 style={{ color: "green", marginBottom: "6px" }}>
                              Present Students (
                              {session.present?.data?.length || 0})
                            </h4>
                            {(session.present?.data || []).map((student) => (
                              <div key={student.id} className="student-card">
                                <span>
                                  {student.student_id} - {student.student_name}
                                </span>
                                <span className="student-status present">
                                  Present
                                </span>
                              </div>
                            ))}
                          </div>

                          {/* Absent Students */}
                          <div style={{ marginTop: "12px" }}>
                            <h4 style={{ color: "red", marginBottom: "6px" }}>
                              Absent Students ({session.absent?.data?.length || 0})
                            </h4>
                            {(session.absent?.data || []).map((student) => (
                              <div key={student.id} className="student-card">
                                <span>
                                  {student.student_id} - {student.student_name}
                                </span>
                                <span className="student-status absent">
                                  Absent
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "20px",
                  }}
                >
                  <p style={{ color: "gray", fontSize: "16px" }}>
                    Loading sessions...
                  </p>
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <p style={{ color: "red", fontSize: "16px" }}>
                Please select a location to view sessions.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}